/* eslint-disable no-param-reassign */

import { pick, flatten, isEqual } from 'lodash-es';
import { create } from 'jsondiffpatch';
import sendToPreviewIframes from './live-preview-messaging';

// Properties not currently managed via deltas,
// and thus must be saved manually when changing.
const SettingsFields = [
  'playing',
  'videoPlaying',
  'muted',
  'looped',
  'backgroundColor',
  'backgroundType',
  'backgroundImage',
  'backgroundVideo',
  'transition',
  'transitionMode',
];

const clone = (obj) => JSON.parse(JSON.stringify(obj));

const flattenTimelineForPlayback = (timeline = []) => timeline.reduce((memo, item) => {
  if (!item || !Object.keys(item).length) {
    return memo;
  }

  if (['segment', 'marker'].includes(item.layout)) {
    return memo.concat(flattenTimelineForPlayback(item.posts || []));
  }

  memo.push(item);
  return memo;
}, []);

// Create a timeline patcher that uses timeline_id to match array items
const diffpatcher = create({
  objectHash: (obj, index) => (
    obj?.timeline_id || `$$index:${index}`
  ),
});

// Track previous state of production before user changes things.
// Used to calculate delta
let prevProduction;

// helper functions that need a new home sometime in the future
function isIdOnlyPostPlaceholder(item) {
  // Compatibility for legacy ID-only, non-social posts and stale duplicates.
  // These placeholders are not supported standalone timeline children.
  // Apply this compatibility check to nested posts, not root validation.
  return Boolean(item?.timeline_id && Object.keys(item).length === 1);
}

function getItemFromTimeline(timelineId, timeline, parentTimelineId = null) {
  if (!timeline?.length) {
    return null;
  }

  for (let i = 0; i < timeline.length; i += 1) {
    const item = timeline[i];

    if (item && !(parentTimelineId && isIdOnlyPostPlaceholder(item))) {
      if (item.timeline_id === timelineId) {
        return item;
      }

      if (item.posts?.length) {
        const nested = getItemFromTimeline(timelineId, item.posts, item.timeline_id);
        if (nested) {
          return nested;
        }
      }
    }
  }

  return null;
}

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

/**
 * Filter references from nested posts, including stale graphic references
 * left by older duplicates. References represent "no queued post", not
 * distinct timeline items. Full nested items retain their existing handling.
 * @param {Array<Object>} items - List of posts (or root timeline items).
 * @param {String|null} parentTimelineId - Timeline ID of the container.
 * @returns {Array<Object>}
 */
function withoutPostPlaceholders(items, parentTimelineId) {
  const list = items || [];

  return parentTimelineId
    ? list.filter((item) => (
      item?.timeline_id !== parentTimelineId && !isIdOnlyPostPlaceholder(item)
    ))
    : list;
}

/**
 * Build a map of every timeline item (including nested posts) by timeline_id.
 * @param {Array<Object>} timeline - Timeline array.
 * @param {String|null} parentTimelineId - Timeline ID of the parent container.
 * @returns {Map<String, {item: Object, index: Number, parentTimelineId: String|null}>}
 */
const collectTimelineItems = (timeline = [], parentTimelineId = null) => {
  const map = new Map();

  timeline.forEach((item, index) => {
    if (!item?.timeline_id) {
      return;
    }

    // Skip nested placeholders, including stale IDs, but keep the raw index so
    // consumers insert at the same position in the unfiltered `posts` array.
    if (parentTimelineId && (
      item.timeline_id === parentTimelineId || isIdOnlyPostPlaceholder(item)
    )) {
      return;
    }

    map.set(item.timeline_id, { item, index, parentTimelineId });

    if (item.posts?.length) {
      const nested = collectTimelineItems(item.posts, item.timeline_id);
      nested.forEach((value, key) => map.set(key, value));
    }
  });

  return map;
};

/**
 * Compare two versions of the same timeline item and return a list of
 * key/value changes relative to that item.
 * @param {Object} prev - Previous item.
 * @param {Object} current - Current item.
 * @param {Array<String>} [path] - Current key path.
 * @returns {Array<{key: Array<String>, value: any}>}
 */
const getLeafChanges = (prev, current, path = []) => {
  const changes = [];

  if (prev === current) {
    return changes;
  }

  if (isPlainObject(prev) && isPlainObject(current)) {
    const keys = new Set([...Object.keys(prev), ...Object.keys(current)]);

    keys.forEach((key) => {
      if (['__proto__', 'constructor', 'prototype'].includes(key)) {
        return;
      }

      // The item's own container `posts` array is represented by
      // add/remove/order events, so skip it at the item root regardless of
      // whether it currently exists. Social post lists nested under
      // `fields.*.posts` are NOT timeline items, so those still need to be
      // propagated as changes.
      if (key === 'posts' && path.length === 0) {
        return;
      }

      changes.push(...getLeafChanges(prev[key], current[key], path.concat(key)));
    });

    return changes;
  }

  if (Array.isArray(prev) && Array.isArray(current)) {
    if (JSON.stringify(prev) !== JSON.stringify(current)) {
      changes.push({ key: path, value: current });
    }

    return changes;
  }

  changes.push({ key: path, value: current === undefined ? null : current });
  return changes;
};

/**
 * Generate typed timeline events for the changes between two productions.
 * @param {Object} prevProduction - Previous production state.
 * @param {Object} currentProduction - Current production state.
 * @returns {Array<{type: String, [key: string]: any}>}
 */
const getChangedTimelineEvents = (prevProd = {}, currentProd = {}) => {
  const events = [];
  const prevTimeline = prevProd.timeline || [];
  const currentTimeline = currentProd.timeline || [];
  const prevMap = collectTimelineItems(prevTimeline);
  const currentMap = collectTimelineItems(currentTimeline);

  // Deletions. Only emit for the top of each removed subtree (removing a
  // container removes its nested posts). Emitted as a single bulk event so
  // clearing/bulk-removing a timeline doesn't produce N events/messages.
  const removedTimelineIds = [];
  prevMap.forEach(({ parentTimelineId }, timelineId) => {
    if (currentMap.has(timelineId)) {
      return;
    }

    const parentIsGone = Boolean(parentTimelineId && !currentMap.has(parentTimelineId));
    if (!parentIsGone) {
      removedTimelineIds.push(timelineId);
    }
  });

  if (removedTimelineIds.length === 1) {
    events.push({ type: 'delete', timelineId: removedTimelineIds[0] });
  } else if (removedTimelineIds.length > 1) {
    events.push({ type: 'delete', timelineIds: removedTimelineIds });
  }

  // Additions.
  currentMap.forEach(({ item, index, parentTimelineId }, timelineId) => {
    if (prevMap.has(timelineId)) {
      return;
    }

    const parentIsNew = Boolean(parentTimelineId && !prevMap.has(parentTimelineId));
    if (!parentIsNew) {
      events.push({
        type: 'add',
        timelineId,
        item,
        index,
        parentTimelineId,
      });
    }
  });

  // Reordering for root and nested containers.
  const collectContainerOrders = (prevItems = [], currentItems = [], parentTimelineId = null) => {
    const prevList = withoutPostPlaceholders(prevItems, parentTimelineId);
    const currentList = withoutPostPlaceholders(currentItems, parentTimelineId);
    const prevIds = prevList.map((item) => item?.timeline_id).filter(Boolean);
    const currentIds = currentList.map((item) => item?.timeline_id).filter(Boolean);

    if (JSON.stringify(prevIds) !== JSON.stringify(currentIds)) {
      events.push({ type: 'order', timelineIds: currentIds, parentTimelineId });
    }

    const currentById = new Map(currentList.map((item) => [item?.timeline_id, item]));
    const prevById = new Map(prevList.map((item) => [item?.timeline_id, item]));

    currentList.forEach((item) => {
      if (item?.posts?.length) {
        collectContainerOrders(
          prevById.get(item.timeline_id)?.posts || [],
          currentById.get(item.timeline_id)?.posts || [],
          item.timeline_id,
        );
      }
    });
  };

  // Cross-container moves (reparenting). `add`/`delete` don't cover these
  // because the timeline_id still exists in both states, and `order` only
  // reorders items within a container. Emit an explicit atomic move so items
  // don't stay at / snap back to their previous container on refresh.
  currentMap.forEach(({ index, parentTimelineId }, timelineId) => {
    const prevEntry = prevMap.get(timelineId);
    if (!prevEntry) {
      return; // handled as an add
    }

    const prevParent = prevEntry.parentTimelineId || null;
    const nextParent = parentTimelineId || null;

    if (prevParent === nextParent) {
      return; // no reparent; order/change events cover it
    }

    // When the new parent is itself brand new, its `add` event already
    // carries this item as part of its subtree, so skip to avoid doubles.
    if (nextParent && !prevMap.has(nextParent)) {
      return;
    }

    events.push({
      type: 'move',
      timelineId,
      parentTimelineId: nextParent,
      index,
    });
  });

  // Reparent first so nested orders preserve placeholder slots after detach/insert.
  collectContainerOrders(prevTimeline, currentTimeline, null);

  // Field-level changes.
  currentMap.forEach(({ item }, timelineId) => {
    const prevItem = prevMap.get(timelineId)?.item;
    if (!prevItem) {
      return;
    }

    getLeafChanges(prevItem, item).forEach(({ key, value }) => {
      events.push({ type: 'change', timelineId, key, value });
    });
  });

  return events;
};

/**
 * Build a settings event for changed root production properties.
 * These are sent separately from deltas so new clients can update
 * index/sub_index and other root fields without processing a delta.
 * @param {Object} prevProduction - Previous production state.
 * @param {Object} currentProduction - Current production state.
 * @returns {Object} Changed production settings.
 */
const getChangedProductionSettings = (prevProd = {}, currentProd = {}) => {
  const fields = [
    ...SettingsFields,
    'index',
    'sub_index',
    'timelineLocked',
  ];

  return fields.reduce((memo, field) => {
    // Use a value comparison so object fields (e.g. `backgroundVideo`) aren't
    // reported as changed on every call just because they were cloned.
    if (!isEqual(prevProd[field], currentProd[field])) {
      memo[field] = currentProd[field];
    }

    return memo;
  }, {});
};

const normalizeType = (layout) => ({
  graphic: 'Graphic',
  panel: 'Panel',
  poll: 'Poll',
  theme: 'Theme',
}[layout] || 'Theme');

const isTheme = (layout) => !['graphic', 'graphics', 'panel', 'asset', 'poll', 'marker'].includes(layout);

const isAlreadyQueued = (parent, post, timeline) => {
  const item = getItemFromTimeline(parent, timeline);
  return (item?.queuedPost === post);
};

/**
 * Checks if timeline item is a social graphic,
 * and returns the social element's fields map if there is one as an array of length 1.
 * If item is a graphics group, it will return an array of all social element field maps found.
 * @param {Object} item - Timeline item
 * @returns {Array<Object>} - List of field maps for all social graphic layers found.
 */
const getSocialFields = (item) => {
  if (item.layout === 'graphic') {
    // Check for both social-data (old) and social-display (new) elements
    const [socialDataId] = item.meta?.typeLookup?.['social-data'] || [];
    const [socialDisplayId] = item.meta?.typeLookup?.['social-display'] || [];
    const id = socialDataId || socialDisplayId;
    if (id) {
      return [item.fields[id]];
    }
  }

  if (item.layout === 'graphics') {
    return item.posts.reduce((memo, post) => {
      // Check for both social-data (old) and social-display (new) elements
      const [socialDataId] = post.meta?.typeLookup?.['social-data'] || [];
      const [socialDisplayId] = post.meta?.typeLookup?.['social-display'] || [];
      const id = socialDataId || socialDisplayId;
      if (id) {
        memo.push(post.fields[id]);
      }

      return memo;
    }, []);
  }

  if (item.layout === 'segment') {
    return flatten(
      item.posts.map((post) => getSocialFields(post)),
    );
  }

  return [];
};

/**
 * Checks if timeline item contains any social posts that are selected.
 * If a social post is queued, then set the sub_index to that post's timeline_id.
 * Otherwise the sub_index will just match the index.
 * @param {String} timelineId - ID of the live element to get the sub_index for.
 * @param {Array<Object>} timeline - Full production timeline.
 * @returns {String} sub_index to use
 */
const determineSubIndex = (item) => {
  if (!item) {
    return null;
  }

  const {
    posts,
    queuedPost,
  } = item;

  const [post] = posts || [];

  // If there's a social post and it's a tagboard...
  if (post?.type === 'tagboard') {
    // then return the tagboard's timeline_id
    return post.timeline_id;
  }

  // If the timeline item has a queuedPost, then use that.
  // This only covers queued posts for themes.
  // NOTE: Shouldn't have to check layout here; idk how queuedPost gets set on graphics groups
  if (item.layout !== 'graphics' && queuedPost) {
    return queuedPost;
  }

  // If timeline item is a social graphic,
  // then we need to check fields for a queuedPost
  const socialFields = getSocialFields(item);
  if (socialFields.length) {
    // Look for the first queuedPost we can find if there is one
    for (let i = 0; i < socialFields.length; i += 1) {
      const fields = socialFields[i] || {};
      const [p] = fields.posts || [];

      // Ignore graphics with full tagboards in them
      if (p?.type !== 'tagboard' && fields.queuedPost) {
        return fields.queuedPost;
      }
    }
  }

  return item.timeline_id;
};

/**
 * Ensure timeline item isn't an empty object and contains `posts` key
 */
const validateTimelineItem = (item) => (
  item
  && Object.keys(item).length
  && (item.layout === 'marker' || item.posts)
);

/**
 * Determine whether a partial deltas object contains any actual changes.
 * @param {Object} partialDeltas - Partial deltas object.
 * @returns {Boolean}
 */
const hasPartialDeltaChanges = (partialDeltas) => Boolean(
  partialDeltas
  && (
    partialDeltas.timelineDelta
    || partialDeltas.productionDelta
    || Object.keys(partialDeltas.timelineItemDeltas || {}).length
  ),
);

/**
 * Defensively drop corrupt entries from nested posts arrays (graphics-group
 * and segment children). Race conditions in array-index-based socket delta
 * merging can leave `null` entries or empty `{}` objects in these arrays.
 * Empty objects are truthy, so a plain `filter(Boolean)` keeps them, but
 * they have no keys and break consumers that read `layout`, `fields`, or
 * `meta` off nested items, so they're unambiguously invalid.
 * Cleans items in place and recurses into any nested containers.
 * @param {Array<Object>} items - Timeline items to clean in place
 */
const cleanNestedPosts = (items = []) => {
  items.forEach((item) => {
    if (!item) {
      return;
    }

    if (Array.isArray(item.posts)) {
      item.posts = item.posts.filter((post) => (
        post && Object.keys(post).length > 0
      ));

      cleanNestedPosts(item.posts);
    }
  });
};

/**
 * Remove duplicate timeline items (matched by timeline_id) from the top-level
 * timeline and every nested posts array. timeline_ids are unique per
 * production, so duplicates are corruption introduced by race conditions in
 * delta merging: cross-list drags briefly hold the dragged item in both the
 * source and destination arrays (Sortable fires the destination's `add`
 * before the source's `remove`), and an echoed delta re-applied over that
 * window inserts the same item twice. One shared seen-set is carried through
 * the entire tree, so the first-encountered occurrence (in traversal order)
 * wins across the root, segments, and graphics groups. Falsy items are
 * ignored, ordering is otherwise preserved, and items are cleaned in place.
 * @param {Array<Object>} items - Timeline items to dedupe in place
 * @param {Set} [seen] - Internal set of timeline_ids already encountered
 */
const dedupeTimelineItems = (items = [], seen = new Set()) => {
  const deduped = [];

  items.forEach((item) => {
    if (!item) {
      return;
    }

    if (item.timeline_id && seen.has(item.timeline_id)) {
      // Duplicate timeline_id within this container is corruption.
      return;
    }

    if (item.timeline_id) {
      seen.add(item.timeline_id);
    }

    if (Array.isArray(item.posts)) {
      dedupeTimelineItems(item.posts, seen);
    }

    deduped.push(item);
  });

  items.length = 0;
  items.push(...deduped);
};

const getChanges = (production) => diffpatcher.diff(prevProduction, production);

/**
 * Generate partial deltas for improved merge handling.
 * Returns an object with three deltas:
 * - timelineDelta: Diff of timeline_ids for tracking additions/deletions/reordering
 * - productionDelta: Diff of production sans timeline
 * - timelineItemDeltas: Map of diffs for each changed timeline item
 * @param {Object} prevProd - Previous production state
 * @param {Object} currentProd - Current production state
 * @returns {Object} Partial deltas object
 */
const generatePartialDeltas = (prevProd, currentProd) => {
  if (!prevProd || !currentProd) {
    return null;
  }

  // 1. Generate diff of timeline_ids
  const prevTimelineIds = (prevProd.timeline || []).map((item) => item.timeline_id);
  const currentTimelineIds = (currentProd.timeline || []).map((item) => item.timeline_id);
  const timelineDelta = diffpatcher.diff(prevTimelineIds, currentTimelineIds);

  // 2. Generate diff of production sans timeline
  const prevProdSansTimeline = { ...prevProd };
  delete prevProdSansTimeline.timeline;
  const currentProdSansTimeline = { ...currentProd };
  delete currentProdSansTimeline.timeline;
  const productionDelta = diffpatcher.diff(prevProdSansTimeline, currentProdSansTimeline);

  // 3. Generate diff for each individual timeline item that changed
  const timelineItemDeltas = {};
  const currentTimelineMap = {};
  const prevTimelineMap = {};

  (currentProd.timeline || []).forEach((item) => {
    if (item?.timeline_id) {
      currentTimelineMap[item.timeline_id] = item;
    }
  });

  (prevProd.timeline || []).forEach((item) => {
    if (item?.timeline_id) {
      prevTimelineMap[item.timeline_id] = item;
    }
  });

  // Check each timeline item for changes
  Object.keys(currentTimelineMap).forEach((timelineId) => {
    const currentItem = currentTimelineMap[timelineId];
    const prevItem = prevTimelineMap[timelineId];

    if (!prevItem) {
      // New item, include full diff
      timelineItemDeltas[timelineId] = diffpatcher.diff({}, currentItem);
    } else {
      // Existing item, check if it changed
      const itemDelta = diffpatcher.diff(prevItem, currentItem);
      if (itemDelta) {
        timelineItemDeltas[timelineId] = itemDelta;
      }
    }
  });

  return {
    timelineDelta,
    productionDelta,
    timelineItemDeltas,
  };
};

/**
 * Determine whether there are unsent local changes.
 * @param {Object} production - Current production state.
 * @returns {Boolean}
 */
const hasPendingChanges = (production) => (
  hasPartialDeltaChanges(generatePartialDeltas(prevProduction, production))
);

/**
 * Merge provided object into prevProduction.
 * Mainly used to catch prevProduction up with any changes,
 * but not completely overwriting it.
 * @param {Object} partial - Object to merge into prevProduction
 */
const updatePrevProduction = (partial) => {
  prevProduction = Object.assign(prevProduction, partial);
};

/**
 * Overwrite prevProduction with a clone of the provided production
 * @param {Object} production - Production to clone and store
 */
const setPrevProduction = (production) => {
  prevProduction = clone(production);
};

/**
 * Get list of social posts attached to the timeline item.
 * For themes and single graphics, this is just the array of posts found on them.
 * For grouped graphics, returned value will be a concatenated list of all social posts
 * found in all graphics in the group.
 * @param {Object} item - Timeline item
 * @returns {Array<Object>} List of social posts and/or tagboards
 */
const getSocialPosts = (item) => {
  if (!item) {
    return [];
  }

  switch (item.layout) {
    case 'graphic': {
      // Check for both social-data (old) and social-display (new) elements
      const [socialDataId] = item.meta?.typeLookup?.['social-data'] || [];
      const [socialDisplayId] = item.meta?.typeLookup?.['social-display'] || [];
      const elementId = socialDataId || socialDisplayId;
      if (!elementId) { return []; }

      const fields = item.fields[elementId] || {};
      return fields?.posts || [];
    }

    case 'graphics': {
      return flatten(
        (item.posts || [])
          .filter((it) => it && Object.keys(it).length > 0)
          .map((it) => getSocialPosts(it)),
      );
    }

    case 'segment':
    case 'marker': {
      return flatten(
        (item.posts || [])
          .filter((it) => it && Object.keys(it).length > 0)
          .map((it) => getSocialPosts(it)),
      );
    }

    default: {
      return item.posts;
    }
  }
};

let updateTimelineTimeout;

/**
 * Calculate diff of production and send changes over the socket
 * @param {Object} state
 * @param {Object} rootState
 * @param {boolean} [preview]
 */
const updateTimeline = (state, rootState, opts = {}) => {
  const {
    preview = false,
    force = false,
  } = opts;

  if (window._tgbSaving) {
    // Wait and try again in 0.1s
    clearTimeout(updateTimelineTimeout);
    updateTimelineTimeout = setTimeout(() => {
      updateTimeline(state, rootState, opts);
    }, 100);

    return;
  }

  // Global variable used for integration tests
  window._tgbSaving = true;

  try {
    const prodIndex = preview ? preview.index : state.production.index;
    const layout = getItemFromTimeline(prodIndex, state.production.timeline);

    const prodSubIndex = preview ? preview.sub_index : state.production.sub_index;

    const posts = getSocialPosts(layout);

    const post = (posts || []).find((p) => (
      p.timeline_id === prodSubIndex
    ));

    const live = {
      layout,
      post,
    };

    // TODO: We should probably merge fields into settings for smart panels
    // tgb-live will do it too, but for backwards compatibility with old displays...

    if (isTheme(live.layout?.layout) && live.post) {
      // We'll attach whatever post we have here in producer,
      // but tgb-live shouldn't rely on it.
      // It should still fetch the post if the data is missing.
      const postRecord = rootState.posts.lookupTable[post.post_id];
      if (post?.post_id && postRecord) {
        // Giving this info to tgb-live will save us a network request,
        // but tgb-live shouldn't rely on it
        live.post = {
          ...post,
          ...postRecord,

          // This flag tells live to not bother loading the post
          loaded: true,
        };
      }
    }

    let timelineIds;
    let partialDeltas;
    let timelineEvents = [];
    let productionSettings = {};

    if (!preview) {
      // Generate partial deltas (timeline_id-based) instead of a legacy
      // full-production jsondiffpatch delta.
      partialDeltas = generatePartialDeltas(prevProduction, state.production);

      // Generate targeted timeline events and root production settings
      // for clients that no longer process jsondiffpatch partialDeltas.
      timelineEvents = getChangedTimelineEvents(prevProduction, state.production);
      productionSettings = getChangedProductionSettings(prevProduction, state.production);

      // Track affected timeline IDs for server-side error context.
      timelineIds = timelineEvents.reduce((memo, event) => {
        if (event.timelineId) {
          memo.push(event.timelineId);
        }

        if (Array.isArray(event.timelineIds)) {
          memo.push(...event.timelineIds);
        }

        return memo;
      }, []);

      setPrevProduction(state.production);
    }

    // When data is sent through here with preview = true
    // the data will be sent to the updatePreviewTimeline action
    // this way it'll update the connected screens on the preview room
    const data = {
      production_id: state.production._id,
      owner: state.production.owner,
      user_id: rootState.user.id,
      partialDeltas,
      metadata: { timelineIds },
      force,

      // NOTE: `live` is redundant since outputs can deduce the live layout using the timeline
      //       Remove whenever tgb-live is no longer reliant on it
      live,
      display_token: state.production.display_token,
    };

    if (preview) {
      data.preview = true;

      // Gotta attach these since preview doesn't handle deltas the same way
      data.index = preview.index;
      data.sub_index = preview.sub_index;
      data.videoPlaying = preview.videoPlaying;

      // NOTE: It sucks to send the entire timeline again,
      // but easier than setting up two separate deltas (for now)
      data.timeline = state.production.timeline;
    } else if (!hasPartialDeltaChanges(partialDeltas) && !force) {
      // If attempting to send timeline update to live without actual changes
      // or a force flag, then just skip the message entirely, since it won't do anything.
      return;
    }

    if (!preview) {
      // Emit targeted timeline events for new clients. Legacy clients
      // still receive the full delta below.
      timelineEvents.forEach((event) => {
        const { type, ...payload } = event;

        switch (type) {
          case 'change':
            $socket.emit('updateTimelineItem', {
              production_id: state.production._id,
              user_id: rootState.user.id,
              timelineId: payload.timelineId,
              key: payload.key,
              value: payload.value,
            });

            sendToPreviewIframes('updateTimelineItem', {
              production_id: state.production._id,
              user_id: rootState.user.id,
              timelineId: payload.timelineId,
              key: payload.key,
              value: payload.value,
            }, { $frameTarget: true, live: true, preview: false });

            break;

          case 'add':
            $socket.emit('addTimelineItem', {
              production_id: state.production._id,
              user_id: rootState.user.id,
              timelineId: payload.timelineId,
              item: payload.item,
              index: payload.index,
              parentTimelineId: payload.parentTimelineId,
            });

            sendToPreviewIframes('addTimelineItem', {
              production_id: state.production._id,
              user_id: rootState.user.id,
              timelineId: payload.timelineId,
              item: payload.item,
              index: payload.index,
              parentTimelineId: payload.parentTimelineId,
            }, { $frameTarget: true, live: true, preview: false });

            break;

          case 'delete': {
            const isBulk = Array.isArray(payload.timelineIds);
            const eventName = isBulk ? 'removeTimelineItems' : 'removeTimelineItem';
            const eventPayload = isBulk
              ? {
                production_id: state.production._id,
                user_id: rootState.user.id,
                timelineIds: payload.timelineIds,
              }
              : {
                production_id: state.production._id,
                user_id: rootState.user.id,
                timelineId: payload.timelineId,
              };

            $socket.emit(eventName, eventPayload);
            sendToPreviewIframes(eventName, eventPayload, { $frameTarget: true, live: true, preview: false });
            break;
          }

          case 'order':
            $socket.emit('updateTimelineOrder', {
              production_id: state.production._id,
              user_id: rootState.user.id,
              timelineIds: payload.timelineIds,
              parentTimelineId: payload.parentTimelineId,
            });

            sendToPreviewIframes('updateTimelineOrder', {
              production_id: state.production._id,
              user_id: rootState.user.id,
              timelineIds: payload.timelineIds,
              parentTimelineId: payload.parentTimelineId,
            }, { $frameTarget: true, live: true, preview: false });

            break;

          case 'move':
            $socket.emit('moveTimelineItem', {
              production_id: state.production._id,
              user_id: rootState.user.id,
              timelineId: payload.timelineId,
              parentTimelineId: payload.parentTimelineId,
              index: payload.index,
            });

            sendToPreviewIframes('moveTimelineItem', {
              production_id: state.production._id,
              user_id: rootState.user.id,
              timelineId: payload.timelineId,
              parentTimelineId: payload.parentTimelineId,
              index: payload.index,
            }, { $frameTarget: true, live: true, preview: false });

            break;

          default:
            break;
        }
      });

      if (Object.keys(productionSettings).length) {
        $socket.emit('updateProduction', {
          production_id: state.production._id,
          user_id: rootState.user.id,
          ...productionSettings,
        });

        sendToPreviewIframes('timelineSettings', {
          production_id: state.production._id,
          ...productionSettings,
        }, { $frameTarget: true, live: true, preview: false });
      }

      // New clients ignore these partialDeltas, but old clients still rely on them.
      if (hasPartialDeltaChanges(partialDeltas)) {
        data.version = 2;
      }
    }

    $socket.emit('updateTimeline', data);
    sendToPreviewIframes('timeline', data, { $frameTarget: true, live: !preview, preview });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('Error updating timeline:', err);
  } finally {
    // Global variable used for integration tests
    window._tgbSaving = false;
  }
};

const sendSettings = (state) => {
  const { production } = state;

  $socket.emit(
    'settings',
    {
      production_id: production._id,
      ...pick(production, SettingsFields),
    },
  );

  sendToPreviewIframes('timelineSettings', {
    production_id: production._id,
    ...pick(production, SettingsFields),
  }, { $frameTarget: true, live: true, preview: false });
};

/**
 * Get previous item and/or post given the current index and subIndex.
 * @param {Object[]} opts.timeline - Current production timeline
 * @param {string} opts.index - timeline_id of current live item
 * @param {string} opts.subIndex - timeline_id of current live post (nested inside item)
 * @param {boolean} opts.looped - Whether or not to loop around when at the start
 * @returns {Object} Object with previous item/post index and subIndex
 */
const getPreviousItemOrPost = (opts = {}) => {
  const {
    timeline,
    index,
    subIndex,
    looped,
  } = opts;

  const flattenedTimeline = flattenTimelineForPlayback(timeline);
  const itemCount = flattenedTimeline.length;

  const currentItemIdx = flattenedTimeline.findIndex((i) => i.timeline_id === index);

  if (!itemCount) {
    return {
      index,
      subIndex,
    };
  }

  if (currentItemIdx < 0) {
    const [firstItem] = flattenedTimeline;
    const firstPosts = getSocialPosts(firstItem);

    return {
      index: firstItem.timeline_id,
      subIndex: firstPosts?.length ? firstPosts[0].timeline_id : firstItem.timeline_id,
    };
  }

  const currentItem = flattenedTimeline[currentItemIdx];

  const posts = getSocialPosts(currentItem);
  const currentPostIdx = (index !== subIndex)
    ? posts?.findIndex((p) => p.timeline_id === subIndex)
    : -1;

  const prev = {
    index: (() => {
      if (currentPostIdx > 0) {
        // Stay on current item if not on first post
        return index;
      }

      if (currentItemIdx === 0) {
        if (!looped) {
          // Stay right here
          return index;
        }

        // Loop around to last timeline item
        return flattenedTimeline[itemCount - 1].timeline_id;
      }

      // Move back one item
      return flattenedTimeline[currentItemIdx - 1].timeline_id;
    })(),

    subIndex: (() => {
      if (currentPostIdx > 0) {
        // Move back one post
        return posts[currentPostIdx - 1].timeline_id;
      }

      if (currentItemIdx === 0 && !looped) {
        // Stay right here, since looping is disabled
        return subIndex;
      }

      const prevItem = currentItemIdx === 0
        ? flattenedTimeline[itemCount - 1]
        : flattenedTimeline[currentItemIdx - 1];

      // We know something has nested posts if they have different timeline_ids
      // Graphics groups uses `posts`, but they all render together instead of sequentially
      const prevPosts = getSocialPosts(prevItem);
      if (!prevPosts?.length) {
        // Just set sub_index to same value as index
        return prevItem.timeline_id;
      }

      // Set to final post on previous timeline item (if applicable)
      return prevPosts[prevPosts.length - 1].timeline_id;
    })(),
  };

  return prev;
};

/**
 * Get next item and/or post given the current index and subIndex.
 * @param {Object[]} opts.timeline - Current production timeline
 * @param {string} opts.index - timeline_id of current live item
 * @param {string} opts.subIndex - timeline_id of current live post (nested inside item)
 * @param {boolean} opts.looped - Whether or not to loop around to the first item
 * @returns {Object} Object with next item/post index and subIndex
 */
const getNextItemOrPost = (opts = {}) => {
  const {
    timeline,
    index,
    subIndex,
    looped,
  } = opts;

  const flattenedTimeline = flattenTimelineForPlayback(timeline);
  const itemCount = flattenedTimeline.length;

  const getDefaultSubIndex = (item) => {
    const itemPosts = getSocialPosts(item);
    if (!itemPosts?.length) {
      return item.timeline_id;
    }

    return itemPosts[0].timeline_id;
  };

  // Timeline Item + Index
  const currentItemIdx = flattenedTimeline.findIndex((i) => i.timeline_id === index);

  if (!itemCount || currentItemIdx < 0) {
    return {
      index,
      subIndex,
    };
  }

  const currentItem = flattenedTimeline[currentItemIdx];

  const posts = getSocialPosts(currentItem);
  const postCount = posts.length;

  // Post Index
  const currentPostIdx = (index !== subIndex)
    ? posts.findIndex((p) => p.timeline_id === subIndex)
    : -1;

  const next = {
    index: (() => {
      if (currentPostIdx >= 0 && currentPostIdx < postCount - 1) {
        // Moving to next post, so stay on same item
        return index;
      }

      if (currentItemIdx >= itemCount - 1) {
        if (!looped) {
          // No looping, so stay right here
          return index;
        }

        // Wrap around to beginning
        return flattenedTimeline[0].timeline_id;
      }

      // Move to next timeline item
      return flattenedTimeline[currentItemIdx + 1].timeline_id;
    })(),

    subIndex: (() => {
      if (currentPostIdx >= 0 && currentPostIdx < postCount - 1) {
        // Move forward one post
        return posts[currentPostIdx + 1].timeline_id;
      }

      if (!looped && currentItemIdx >= itemCount - 1) {
        // Just stay on final item and final post
        return subIndex;
      }

      const nextItem = currentItemIdx < itemCount - 1
        ? flattenedTimeline[currentItemIdx + 1] // Next item in playlist
        : flattenedTimeline[0]; // Loop back to beginning

      return getDefaultSubIndex(nextItem);
    })(),
  };

  return next;
};

/**
 * Update `queuedPost` on whatever is currently live, if `sub_index` doesn't match.
 * Applies to social graphics and themes with individual posts.
 * Updates the timeline item in place.
 * @param {Array<Object>} opts.timeline - Current production timeline
 * @param {String} opts.index - Current live layout ID
 * @param {String} opts.subIndex - Current live post (when applicable).
 * @returns {Boolean} True if timeline was updated.
 */
const syncQueuedPost = (opts = {}) => {
  const {
    index,
    subIndex,
    timeline,
  } = opts;

  if (!index || index === subIndex) {
    return false;
  }

  // If sub_index is different from index,
  // and it's a graphic then we should update queuedPost on the graphic
  const item = getItemFromTimeline(index, timeline);

  switch (item?.layout) {
    case 'graphics': {
      // Loop over child graphics, reset queuedPost to null
      // then set queuedPost to the proper post if the graphic contains
      // the new post (i.e. matches sub_index)
      for (let i = 0; i < item.posts.length; i += 1) {
        const graphic = item.posts[i];

        // Skip corrupt entries (e.g. empty objects from a bad delta merge)
        // that lack the fields/meta needed to resolve social elements.
        if (graphic && Object.keys(graphic).length) {
          // Check for both social-data (old) and social-display (new) elements
          const [socialDataId] = graphic.meta?.typeLookup?.['social-data'] || [];
          const [socialDisplayId] = graphic.meta?.typeLookup?.['social-display'] || [];
          const id = socialDataId || socialDisplayId;

          // Optional-chain fields: graphics may be missing the fields map
          // (e.g. partial/corrupt items) even when they carry meta.
          const fields = graphic.fields?.[id];

          if (fields && fields.queuedPost !== subIndex) {
            const { posts } = fields;

            if (posts && posts.some((p) => p.timeline_id === subIndex)) {
              fields.queuedPost = subIndex;
            }
          }
        }
      }

      break;
    }

    case 'segment': {
      for (let i = 0; i < item.posts.length; i += 1) {
        const child = item.posts[i];

        if (syncQueuedPost({
          timeline,
          index: child.timeline_id,
          subIndex,
        })) {
          return true;
        }
      }

      break;
    }

    case 'graphic': {
      // Check for both social-data (old) and social-display (new) elements
      const [socialDataId] = item.meta?.typeLookup?.['social-data'] || [];
      const [socialDisplayId] = item.meta?.typeLookup?.['social-display'] || [];
      const id = socialDataId || socialDisplayId;

      // Optional-chain fields: graphics may be missing the fields map
      // (e.g. partial/corrupt items) even when they carry meta.
      const fields = item.fields?.[id];

      if (fields && fields.queuedPost !== subIndex) {
        fields.queuedPost = subIndex;
        return true;
      }

      break;
    }

    default: {
      if (item && isTheme(item.layout) && item.queuedPost !== subIndex) {
        item.queuedPost = subIndex;
        return true;
      }

      break;
    }
  }

  return false;
};

export {
  // Constants
  SettingsFields,

  // Functions
  cleanNestedPosts,
  dedupeTimelineItems,
  determineSubIndex,
  diffpatcher,
  getChanges,
  hasPendingChanges,
  getChangedProductionSettings,
  getChangedTimelineEvents,
  getItemFromTimeline,
  getLeafChanges,
  getNextItemOrPost,
  getPreviousItemOrPost,
  getSocialPosts,
  isAlreadyQueued,
  isPlainObject,
  isIdOnlyPostPlaceholder,
  isTheme,
  normalizeType,
  sendSettings,
  setPrevProduction,
  syncQueuedPost,
  updatePrevProduction,
  updateTimeline,
  validateTimelineItem,
};
